import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";

import { adminApi, adminSettingsApi } from "@profitlens/api-client";
import type { AuditLogOut } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card } from "@profitlens/ui/card";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Spinner } from "@profitlens/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";

import { PageHeader } from "@/components/page-header";
import { Pagination } from "@/components/pagination";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 20;

function ChangePreview({ label, value }: { label: string; value: Record<string, unknown> | null }) {
  if (!value || Object.keys(value).length === 0) return null;
  return (
    <div className="mt-1 text-xs text-muted">
      <span className="font-medium">{label}：</span>
      <code className="whitespace-pre-wrap break-all">{JSON.stringify(value)}</code>
    </div>
  );
}

function AuditRow({ log }: { log: AuditLogOut }) {
  const { t } = useTranslation();
  const hasChange = log.before !== null || log.after !== null;
  return (
    <Fragment>
      <TableRow>
        <TableCell className="text-xs whitespace-nowrap text-muted">
          {formatDateTime(log.created_at)}
        </TableCell>
        <TableCell className="font-medium">{log.tenant_name ?? t("ops.platformLabel")}</TableCell>
        <TableCell>{log.user_label ?? t("ops.typeSystem")}</TableCell>
        <TableCell>
          <Badge variant="outline">{log.action}</Badge>
        </TableCell>
        <TableCell className="text-xs">
          {log.entity ?? "—"}
          {log.entity_id ? ` · ${log.entity_id.slice(0, 8)}` : ""}
        </TableCell>
        <TableCell className="text-xs text-muted">{log.ip ?? "—"}</TableCell>
      </TableRow>
      {hasChange ? (
        <tr className="border-b border-border bg-surface-muted/40 hover:bg-transparent">
          <td colSpan={6} className="px-3 py-2">
            <div className="pl-2 text-xs">
              <ChangePreview label={t("ops.auditBefore")} value={log.before} />
              <ChangePreview label={t("ops.auditAfter")} value={log.after} />
            </div>
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

export function AuditPage() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [tenantId, setTenantId] = useState("");
  const [action, setAction] = useState("");
  const [entity, setEntity] = useState("");
  const [error, setError] = useState<string | null>(null);

  const filters = {
    tenant_id: tenantId || undefined,
    action: action || undefined,
    entity: entity || undefined,
  };

  const list = useQuery({
    queryKey: ["admin", "audit-logs", { ...filters, page }],
    queryFn: () => adminApi.auditLogs({ ...filters, page, page_size: PAGE_SIZE }),
  });

  const exportCsv = useMutation({
    mutationFn: () => adminSettingsApi.exportAuditLogs(filters),
    onSuccess: ({ blob, fileName }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName ?? `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setError(null);
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("ops.auditTitle")}
        subtitle={t("ops.auditSubtitle")}
        actions={
          <Button
            variant="outline"
            onClick={() => exportCsv.mutate()}
            disabled={exportCsv.isPending}
          >
            <Download className="h-4 w-4" />
            {exportCsv.isPending ? t("common.exporting") : t("ops.auditExport")}
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={tenantId}
          onChange={(e) => {
            setTenantId(e.target.value);
            setPage(1);
          }}
          placeholder={t("ops.auditTenantId")}
          className="w-64 font-mono text-xs"
        />
        <Input
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
          placeholder={t("ops.auditActionPlaceholder")}
          className="w-48 font-mono text-xs"
        />
        <Input
          value={entity}
          onChange={(e) => {
            setEntity(e.target.value);
            setPage(1);
          }}
          placeholder={t("ops.auditEntityPlaceholder")}
          className="w-36 font-mono text-xs"
        />
        <p className="text-xs text-muted">{t("ops.auditExportHint")}</p>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}

      {list.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : list.error ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data && list.data.items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted">{t("ops.auditEmpty")}</Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.time")}</TableHead>
                <TableHead>{t("common.tenant")}</TableHead>
                <TableHead>{t("ops.auditUser")}</TableHead>
                <TableHead>{t("ops.auditAction")}</TableHead>
                <TableHead>{t("ops.auditEntity")}</TableHead>
                <TableHead>{t("ops.auditIp")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.data?.items.map((log) => (
                <AuditRow key={log.id} log={log} />
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={list.data?.total ?? 0}
            onPage={setPage}
          />
        </Card>
      )}
    </div>
  );
}
